import React, { useState, useEffect } from 'react';
import { TourFormData, ValidationError, Tour } from '../types';
import { validateForm, validateField, getFieldExamples, fieldValidationRules, MAX_BANNER_BYTES, MAX_BROCHURE_BYTES, isFileSizeError } from '../utils/validation';
import FormField from '../components/admin/FormField';
import CountriesField from '../components/admin/CountriesField';
import MarkdownEditor from '../components/admin/MarkdownEditor';
import AdminNavbar from '../components/admin/AdminNavbar';
import TourList from '../components/admin/TourList';
import { appConfig } from '../config/appConfig';
import { fetchAllToursForAdmin, deleteTour } from '../services/tourService';

const emptyTourForm = (): TourFormData => ({
  name: '',
  tagline: '',
  countries: [],
  price: 0,
  groupSize: undefined,
  duration: 0,
  departure: '',
  arrival: '',
  deadline: '',
  availability: undefined,
  showUrgencyBadge: false,
  urgencySlots: undefined,
  description: '',
  banner: null,
  brochure: null,
  status: 'active',
});

const AdminTourManagementPage: React.FC = () => {
  const [formData, setFormData] = useState<TourFormData>(emptyTourForm());

  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<string | null>(null);
  
  // Update mode state
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [selectedTour, setSelectedTour] = useState<Tour | null>(null);
  const [tours, setTours] = useState<Tour[]>([]);
  const [toursLoading, setToursLoading] = useState(false);

  const examples = getFieldExamples();

  // Fetch all tours on component mount
  useEffect(() => {
    fetchTours();
  }, []);

  const fetchTours = async () => {
    setToursLoading(true);
    try {
      const toursData = await fetchAllToursForAdmin();
      setTours(toursData);
    } catch (error) {
      console.error('Error fetching tours:', error);
      setSubmitStatus('Error fetching tours. Please refresh the page.');
    } finally {
      setToursLoading(false);
    }
  };

  const handleUpdateTour = (tour: Tour) => {
    setSelectedTour(tour);
    setIsUpdateMode(true);
    
    // Populate form with tour data
    setFormData({
      name: tour.tourName,
      tagline: tour.tagline || '',
      countries: Array.isArray(tour.countries) ? tour.countries : [],
      price: tour.price,
      groupSize: tour.maxGroupSize,
      duration: tour.durationDays, // Use duration days as the single duration field
      departure: tour.departureDate ? tour.departureDate.split('T')[0] : '',
      arrival: tour.returnDate ? tour.returnDate.split('T')[0] : '',
      deadline: tour.deadline ? tour.deadline.split('T')[0] : '',
      availability: tour.availableSlots,
      showUrgencyBadge: typeof tour.urgencySlots === 'number',
      urgencySlots: tour.urgencySlots,
      description: tour.description,
      banner: null, // Don't pre-populate files - let user choose new ones
      brochure: null,
      status: tour.status as 'active' | 'inactive' | 'sold_out',
    });
    
    // Clear any existing errors
    setErrors([]);
    setSubmitStatus(null);
    
    // Scroll to form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelUpdate = () => {
    setIsUpdateMode(false);
    setSelectedTour(null);
    setFormData(emptyTourForm());
    setErrors([]);
    setSubmitStatus(null);
  };

  const handleDeleteTour = async (tourId: string) => {
    try {
      const adminToken = btoa(`${appConfig.admin.username}:${appConfig.admin.password}`);
      await deleteTour(tourId, adminToken);
      setSubmitStatus('Tour deleted successfully!');
      
      // Refresh tours list
      await fetchTours();
      
      // Clear any update mode if the deleted tour was being edited
      if (selectedTour && selectedTour.tourId === tourId) {
        handleCancelUpdate();
      }
    } catch (error) {
      console.error('Error deleting tour:', error);
      setSubmitStatus('Error deleting tour. Please try again.');
    }
  };

  const handleFieldChange = (fieldName: keyof TourFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value,
    }));

    if (fieldName === 'banner' || fieldName === 'brochure') {
      const error = validateField(fieldName, value, fieldValidationRules[fieldName]);
      setErrors(prev => {
        const without = prev.filter(e => e.field !== fieldName);
        return error ? [...without, { field: fieldName, message: error }] : without;
      });
      const otherField = fieldName === 'banner' ? 'brochure' : 'banner';
      const otherError = validateField(otherField, formData[otherField], fieldValidationRules[otherField]);
      const stillHasSizeError =
        Boolean(error && isFileSizeError(error)) || Boolean(otherError && isFileSizeError(otherError));
      if (error && isFileSizeError(error)) {
        setSubmitStatus(`${error}. Remove the file to keep the rest of this form.`);
      } else if (!stillHasSizeError) {
        setSubmitStatus(current => (current && isFileSizeError(current) ? null : current));
      }
      return;
    }

    // Clear error for this field when user starts typing
    setErrors(prev => prev.filter(error => error.field !== fieldName));
  };

  const getMaxUploadBytes = (fieldName: string) =>
    fieldName === 'brochure' ? MAX_BROCHURE_BYTES : MAX_BANNER_BYTES;

  const getMaxUploadLabel = (fieldName: string) =>
    fieldName === 'brochure' ? '30MB' : '10MB';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    const validationErrors = validateForm(formData);
    setErrors(validationErrors);

    if (validationErrors.length > 0) {
      const hasOversizedFile = validationErrors.some(
        (error) => (error.field === 'banner' || error.field === 'brochure') && isFileSizeError(error.message)
      );
      setSubmitStatus(
        hasOversizedFile
          ? 'A selected file is too large. Remove it below to keep the rest of this form, then choose a smaller file.'
          : 'Please fix the errors below before submitting.'
      );
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Create admin token for authentication (browser-compatible)
      const adminToken = btoa(`${appConfig.admin.username}:${appConfig.admin.password}`);
      
      if (isUpdateMode && selectedTour) {
        // Update existing tour
        console.log('Starting tour update with data:', formData);
        
        // Create FormData for file uploads (same as create flow)
        const submitData = new FormData();
        
        // Append all form fields with proper data types
        Object.entries(formData).forEach(([key, value]) => {
          if (value instanceof File) {
            console.log(`Adding file: ${key}`, value.name, value.size);
            const maxBytes = getMaxUploadBytes(key);
            if (value.size > maxBytes) {
              throw new Error(`File ${value.name} is too large. Maximum size is ${getMaxUploadLabel(key)}.`);
            }
            submitData.append(key, value);
          } else if (value !== null && value !== undefined) {
            if (key === 'showUrgencyBadge') {
              return;
            }
            if (key === 'urgencySlots' && !formData.showUrgencyBadge) {
              return;
            }
            if (
              (key === 'groupSize' || key === 'availability' || key === 'urgencySlots') &&
              (value === '' || (key === 'groupSize' && value === 0))
            ) {
              return;
            }
            let processedValue = value;
            
            // Handle countries array
            if (key === 'countries' && Array.isArray(value)) {
              // Add each country as Country 1, Country 2, etc.
              value.forEach((country, index) => {
                if (country && country.trim() !== '') {
                  submitData.append(`country${index + 1}`, country.trim());
                }
              });
              return; // Skip the normal processing for countries
            }
            
            // Convert numbers to proper format
            if (['price', 'groupSize', 'duration', 'availability', 'urgencySlots'].includes(key)) {
              processedValue = Number(value);
            }
            
            // Convert dates to ISO format
            if (['departure', 'arrival', 'deadline'].includes(key) && value) {
              // Convert date string (YYYY-MM-DD) to ISO datetime
              const date = new Date(value + 'T00:00:00.000Z');
              processedValue = date.toISOString();
            }
            
            console.log(`Adding field: ${key} = ${processedValue} (type: ${typeof processedValue})`);
            submitData.append(key, String(processedValue));
          }
        });

        if (!formData.showUrgencyBadge) {
          submitData.append('clearUrgencySlots', 'true');
        }

        console.log('Sending update request to /api/tours/' + selectedTour.tourId);
        // Send to API
        const response = await fetch(`/api/tours/${selectedTour.tourId}`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${adminToken}`,
          },
          body: submitData,
          // Don't set Content-Type header - let browser set it for FormData
        });

        console.log('Update response received:', response.status, response.statusText);

        if (!response.ok) {
          let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
          try {
            const errorData = await response.json();
            console.error('API Error Response:', errorData);
            console.error('Full error object:', JSON.stringify(errorData, null, 2));
            
            // Handle validation errors
            if (errorData.error && typeof errorData.error === 'object') {
              if (errorData.error.fieldErrors) {
                const fieldErrors = Object.entries(errorData.error.fieldErrors)
                  .map(([field, errors]) => `${field}: ${Array.isArray(errors) ? errors.join(', ') : errors}`)
                  .join('; ');
                errorMessage = `Validation errors: ${fieldErrors}`;
              } else if (errorData.error.formErrors) {
                errorMessage = `Form errors: ${errorData.error.formErrors.join(', ')}`;
              } else {
                errorMessage = errorData.error.message || JSON.stringify(errorData.error);
              }
            } else {
              errorMessage = errorData.error || errorData.message || errorMessage;
            }
          } catch (parseError) {
            console.error('Failed to parse error response:', parseError);
            const responseText = await response.text();
            console.error('Raw response:', responseText);
            errorMessage = responseText || errorMessage;
          }
          throw new Error(errorMessage);
        }

        const result = await response.json();
        setSubmitStatus(`Tour "${result.name}" updated successfully!`);
        
        // Refresh tours list
        await fetchTours();
        
        // Exit update mode
        handleCancelUpdate();
        
      } else {
        // Create new tour
        console.log('Starting tour creation with data:', formData);
        
        // Create FormData for file uploads
        const submitData = new FormData();
        
        // Append all form fields with proper data types
        Object.entries(formData).forEach(([key, value]) => {
          if (value instanceof File) {
            console.log(`Adding file: ${key}`, value.name, value.size);
            const maxBytes = getMaxUploadBytes(key);
            if (value.size > maxBytes) {
              throw new Error(`File ${value.name} is too large. Maximum size is ${getMaxUploadLabel(key)}.`);
            }
            submitData.append(key, value);
          } else if (value !== null && value !== undefined) {
            if (key === 'showUrgencyBadge') {
              return;
            }
            if (key === 'urgencySlots' && !formData.showUrgencyBadge) {
              return;
            }
            if (
              (key === 'groupSize' || key === 'availability' || key === 'urgencySlots') &&
              (value === '' || (key === 'groupSize' && value === 0))
            ) {
              return;
            }
            let processedValue = value;
            
            // Handle countries array
            if (key === 'countries' && Array.isArray(value)) {
              // Add each country as Country 1, Country 2, etc.
              value.forEach((country, index) => {
                if (country && country.trim() !== '') {
                  submitData.append(`country${index + 1}`, country.trim());
                }
              });
              return; // Skip the normal processing for countries
            }
            
            // Convert numbers to proper format
            if (['price', 'groupSize', 'duration', 'availability', 'urgencySlots'].includes(key)) {
              processedValue = Number(value);
            }
            
            // Convert dates to ISO format
            if (['departure', 'arrival', 'deadline'].includes(key) && value) {
              // Convert date string (YYYY-MM-DD) to ISO datetime
              const date = new Date(value + 'T00:00:00.000Z');
              processedValue = date.toISOString();
            }
            
            console.log(`Adding field: ${key} = ${processedValue} (type: ${typeof processedValue})`);
            submitData.append(key, String(processedValue));
          }
        });

        console.log('Sending request to /api/tours');
        // Send to API
        const response = await fetch('/api/tours', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${adminToken}`,
          },
          body: submitData,
          // Don't set Content-Type header - let browser set it for FormData
        });

        console.log('Response received:', response.status, response.statusText);

        if (!response.ok) {
          let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
          try {
            const errorData = await response.json();
            console.error('API Error Response:', errorData);
            console.error('Full error object:', JSON.stringify(errorData, null, 2));
            
            // Handle validation errors
            if (errorData.error && typeof errorData.error === 'object') {
              if (errorData.error.fieldErrors) {
                const fieldErrors = Object.entries(errorData.error.fieldErrors)
                  .map(([field, errors]) => `${field}: ${Array.isArray(errors) ? errors.join(', ') : errors}`)
                  .join('; ');
                errorMessage = `Validation errors: ${fieldErrors}`;
              } else if (errorData.error.formErrors) {
                errorMessage = `Form errors: ${errorData.error.formErrors.join(', ')}`;
              } else {
                errorMessage = errorData.error.message || JSON.stringify(errorData.error);
              }
            } else {
              errorMessage = errorData.error || errorData.message || errorMessage;
            }
          } catch (parseError) {
            console.error('Failed to parse error response:', parseError);
            const responseText = await response.text();
            console.error('Raw response:', responseText);
            errorMessage = responseText || errorMessage;
          }
          throw new Error(errorMessage);
        }

        const result = await response.json();
        setSubmitStatus(`Tour "${result.name}" created successfully! ID: ${result.id}`);
        
        // Reset form
        setFormData(emptyTourForm());
        
        // Refresh tours list
        await fetchTours();
      }

    } catch (error) {
      const action = isUpdateMode ? 'updating' : 'creating';
      const message = error instanceof Error ? error.message : '';
      if (isFileSizeError(message) || /File too large/i.test(message)) {
        setSubmitStatus(`${message || 'File too large.'} Remove the oversized file to keep the rest of this form.`);
      } else {
        setSubmitStatus(`Error ${action} tour. Please try again.`);
      }
      console.error(`Error ${action} form:`, error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getFieldError = (fieldName: string): string | undefined => {
    return errors.find(error => error.field === fieldName)?.message;
  };

  return (
    <div className="min-h-screen bg-[var(--secondary-pearl)]">
      <AdminNavbar />
      <div className="py-8">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="heading-primary text-[var(--primary-deep-blue)] mb-2">
              Tour Management
            </h1>
            <p className="body-regular text-[var(--secondary-slate)]">
              {isUpdateMode ? `Update tour: ${selectedTour?.tourName}` : 'Create and manage tour packages for Raise Tours'}
            </p>
            {isUpdateMode && (
              <div className="mt-2">
                <button
                  onClick={handleCancelUpdate}
                  className="btn btn-secondary text-sm"
                >
                  Cancel Update
                </button>
              </div>
            )}
          </div>

          {/* Form */}
          <div className="card">
            <div className="card-content">
              <form onSubmit={handleSubmit} className="grid gap-6">
                {/* Basic Information */}
                <div className="grid gap-6">
                  <h2 className="heading-tertiary text-[var(--primary-deep-blue)] border-b border-[var(--neutral-medium-gray)] pb-2">
                    Basic Information
                  </h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      label="Tour Name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={(value) => handleFieldChange('name', value)}
                      error={getFieldError('name')}
                      placeholder="Enter tour name"
                      example={examples.name}
                      required
                    />

                    <FormField
                      label="Tour Tagline"
                      name="tagline"
                      type="text"
                      value={formData.tagline}
                      onChange={(value) => handleFieldChange('tagline', value)}
                      error={getFieldError('tagline')}
                      placeholder="Enter tour tagline"
                      example={examples.tagline}
                      required
                    />
                  </div>

                  <CountriesField
                    label="Countries"
                    name="countries"
                    value={formData.countries}
                    onChange={(countries) => handleFieldChange('countries', countries)}
                    error={getFieldError('countries')}
                    placeholder="Enter country name"
                    example={examples.countries}
                    required
                  />
                </div>

                {/* Pricing & Capacity */}
                <div className="grid gap-6">
                  <h2 className="heading-tertiary text-[var(--primary-deep-blue)] border-b border-[var(--neutral-medium-gray)] pb-2">
                    Pricing & Capacity
                  </h2>
                  <p className="text-sm text-[var(--secondary-slate)]">
                    Group size and available slots are for internal tracking only and are not shown on the website.
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      label="Price (₹)"
                      name="price"
                      type="number"
                      value={formData.price}
                      onChange={(value) => handleFieldChange('price', value)}
                      error={getFieldError('price')}
                      placeholder="Enter price"
                      example={examples.price}
                      validation={fieldValidationRules.price}
                      required
                    />

                    <FormField
                      label="Group Size"
                      name="groupSize"
                      type="number"
                      value={formData.groupSize}
                      onChange={(value) => handleFieldChange('groupSize', value)}
                      error={getFieldError('groupSize')}
                      placeholder="Enter group size"
                      example={examples.groupSize}
                      validation={fieldValidationRules.groupSize}
                      allowEmpty
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-6">
                    <FormField
                      label="Duration"
                      name="duration"
                      type="number"
                      value={formData.duration}
                      onChange={(value) => handleFieldChange('duration', value)}
                      error={getFieldError('duration')}
                      placeholder="Enter duration in days"
                      example={examples.duration}
                      validation={fieldValidationRules.duration}
                    />

                    {/* Duration Display */}
                    {formData.duration && formData.duration > 0 && (
                      <div className="bg-[var(--secondary-cream)] border border-[var(--accent-rose-gold)] rounded-md p-4">
                        <p className="body-regular text-[var(--primary-deep-blue)]">
                          <strong>Duration Display:</strong> {formData.duration}D/{Math.max(0, formData.duration - 1)}N
                        </p>
                        <p className="text-sm text-[var(--secondary-slate)] mt-1">
                          Nights are automatically calculated as (days - 1)
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Dates */}
                <div className="grid gap-6">
                  <h2 className="heading-tertiary text-[var(--primary-deep-blue)] border-b border-[var(--neutral-medium-gray)] pb-2">
                    Important Dates
                  </h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <FormField
                      label="Departure Date"
                      name="departure"
                      type="date"
                      value={formData.departure}
                      onChange={(value) => handleFieldChange('departure', value)}
                      error={getFieldError('departure')}
                      example={examples.departure}
                      required
                    />

                    <FormField
                      label="Arrival Date"
                      name="arrival"
                      type="date"
                      value={formData.arrival}
                      onChange={(value) => handleFieldChange('arrival', value)}
                      error={getFieldError('arrival')}
                      example={examples.arrival}
                      required
                    />

                    <FormField
                      label="Registration Deadline"
                      name="deadline"
                      type="date"
                      value={formData.deadline}
                      onChange={(value) => handleFieldChange('deadline', value)}
                      error={getFieldError('deadline')}
                      example={examples.deadline}
                      required
                    />
                  </div>
                </div>

                {/* Availability & Status */}
                <div className="grid gap-6">
                  <h2 className="heading-tertiary text-[var(--primary-deep-blue)] border-b border-[var(--neutral-medium-gray)] pb-2">
                    Availability & Status
                  </h2>
                  
                  <div className="space-y-6">
                    <div className="space-y-3">
                      <FormField
                        label="Status"
                        name="status"
                        type="select"
                        value={formData.status}
                        onChange={(value) => handleFieldChange('status', value)}
                        error={getFieldError('status')}
                        options={[
                          { value: 'active', label: 'Active' },
                          { value: 'inactive', label: 'Inactive' },
                          { value: 'sold_out', label: 'Sold Out' },
                        ]}
                        required
                      />
                      
                      {/* Status Explanations */}
                      <div className="bg-[var(--secondary-cream)] border border-[var(--accent-rose-gold)] rounded-md p-4">
                        <h4 className="body-small font-medium text-[var(--primary-deep-blue)] mb-2">Status Explanations:</h4>
                        <ul className="space-y-1 text-sm text-[var(--secondary-slate)]">
                          <li className="flex items-start">
                            <span className="inline-block w-2 h-2 bg-green-500 rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
                            <span><strong>Active:</strong> Tour will appear on the main page with full functionality</span>
                          </li>
                          <li className="flex items-start">
                            <span className="inline-block w-2 h-2 bg-red-500 rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
                            <span><strong>Sold Out:</strong> Tour will appear on the main page but without the register button</span>
                          </li>
                          <li className="flex items-start">
                            <span className="inline-block w-2 h-2 bg-gray-500 rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
                            <span><strong>Inactive:</strong> Tour will be archived and hidden from the main page</span>
                          </li>
                        </ul>
                      </div>
                    </div>

                    <FormField
                      label="Available Slots"
                      name="availability"
                      type="number"
                      value={formData.availability}
                      onChange={(value) => handleFieldChange('availability', value)}
                      error={getFieldError('availability')}
                      placeholder="Enter available slots"
                      example={examples.availability}
                      validation={fieldValidationRules.availability}
                      allowEmpty
                    />

                    <div className="space-y-3">
                      <button
                        type="button"
                        className={formData.showUrgencyBadge ? 'btn btn-primary' : 'btn btn-secondary'}
                        onClick={() => {
                          if (formData.showUrgencyBadge) {
                            handleFieldChange('showUrgencyBadge', false);
                            handleFieldChange('urgencySlots', undefined);
                          } else {
                            handleFieldChange('showUrgencyBadge', true);
                            handleFieldChange('urgencySlots', formData.urgencySlots || 3);
                          }
                        }}
                      >
                        {formData.showUrgencyBadge
                          ? 'Hide “Only N slots left” on website'
                          : 'Show “Only N slots left” on website'}
                      </button>

                      {formData.showUrgencyBadge && (
                        <div className="bg-[var(--secondary-cream)] border border-[var(--accent-rose-gold)] rounded-md p-4 space-y-4">
                          <p className="text-sm text-[var(--secondary-slate)]">
                            This badge is for marketing urgency only. It is not tied to internal available slots.
                          </p>
                          <FormField
                            label="Number shown on website"
                            name="urgencySlots"
                            type="number"
                            value={formData.urgencySlots}
                            onChange={(value) => handleFieldChange('urgencySlots', value)}
                            error={getFieldError('urgencySlots')}
                            placeholder="e.g. 3"
                            example={examples.urgencySlots}
                            validation={fieldValidationRules.urgencySlots}
                            allowEmpty
                          />
                          {typeof formData.urgencySlots === 'number' && formData.urgencySlots > 0 && (
                            <div>
                              <div className="body-small font-medium text-[var(--primary-deep-blue)] mb-2">Preview</div>
                              <span className="inline-block px-3 py-1 bg-warning text-white text-sm font-semibold rounded-full shadow-lg">
                                Only {formData.urgencySlots} slots left
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div className="grid gap-6">
                  <h2 className="heading-tertiary text-[var(--primary-deep-blue)] border-b border-[var(--neutral-medium-gray)] pb-2">
                    Description
                  </h2>
                  
                  <MarkdownEditor
                    label="Tour Description"
                    name="description"
                    value={formData.description}
                    onChange={(value) => handleFieldChange('description', value)}
                    error={getFieldError('description')}
                    placeholder="Enter detailed tour description in Markdown format"
                  />
                </div>

                {/* Files */}
                <div className="grid gap-6">
                  <h2 className="heading-tertiary text-[var(--primary-deep-blue)] border-b border-[var(--neutral-medium-gray)] pb-2">
                    Media Files
                  </h2>
                  
                  {/* Current Files Display (only in update mode) */}
                  {isUpdateMode && selectedTour && (
                    <div className="bg-[var(--secondary-cream)] border border-[var(--accent-rose-gold)] rounded-md p-4">
                      <h4 className="body-small font-medium text-[var(--primary-deep-blue)] mb-3">Current Files:</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm font-medium text-[var(--secondary-slate)] mb-1">Banner Image:</p>
                          {selectedTour.imageUrl && selectedTour.imageUrl !== '/images/bg_raise_logo.png' ? (
                            <div className="flex items-center gap-2">
                              <img 
                                src={selectedTour.imageUrl} 
                                alt="Current banner" 
                                className="w-16 h-12 object-cover rounded border"
                              />
                              <span className="text-xs text-[var(--secondary-slate)]">Current banner</span>
                            </div>
                          ) : (
                            <span className="text-xs text-[var(--secondary-slate)]">No banner uploaded</span>
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-[var(--secondary-slate)] mb-1">Brochure:</p>
                          {selectedTour.brochureUrl ? (
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-green-600">✓ PDF available</span>
                              <a 
                                href={selectedTour.brochureUrl} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-xs text-[var(--primary-deep-blue)] hover:underline"
                              >
                                View current brochure
                              </a>
                            </div>
                          ) : (
                            <span className="text-xs text-[var(--secondary-slate)]">No brochure uploaded</span>
                          )}
                        </div>
                      </div>
                      <p className="text-xs text-[var(--secondary-slate)] mt-2">
                        Upload new files below to replace the current ones.
                      </p>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      label="Banner Image"
                      name="banner"
                      type="file"
                      value={formData.banner}
                      onChange={(value) => handleFieldChange('banner', value)}
                      error={getFieldError('banner')}
                      accept="image/*"
                      example={examples.banner}
                    />

                    <FormField
                      label="Brochure (PDF)"
                      name="brochure"
                      type="file"
                      value={formData.brochure}
                      onChange={(value) => handleFieldChange('brochure', value)}
                      error={getFieldError('brochure')}
                      accept=".pdf"
                      example={examples.brochure}
                    />
                  </div>
                  
                  {/* File Upload Status */}
                  {(formData.banner || formData.brochure) && (
                    <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                      <div className="flex justify-between items-center mb-2">
                        <h4 className="body-small font-medium text-blue-800">Files Ready for Upload:</h4>
                        <button
                          type="button"
                          onClick={() => {
                            handleFieldChange('banner', null);
                            handleFieldChange('brochure', null);
                          }}
                          className="text-xs text-blue-600 hover:text-blue-800 underline"
                        >
                          Clear Files
                        </button>
                      </div>
                      <div className="space-y-2">
                        {formData.banner && (
                          <div className="flex items-center justify-between gap-3 text-sm text-blue-700">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></span>
                              <span className="truncate">Banner: {formData.banner.name} ({(formData.banner.size / 1024 / 1024).toFixed(2)} MB)</span>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleFieldChange('banner', null)}
                              className="text-xs text-blue-600 hover:text-blue-800 underline whitespace-nowrap"
                            >
                              Remove
                            </button>
                          </div>
                        )}
                        {formData.brochure && (
                          <div className="flex items-center justify-between gap-3 text-sm text-blue-700">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></span>
                              <span className="truncate">Brochure: {formData.brochure.name} ({(formData.brochure.size / 1024 / 1024).toFixed(2)} MB)</span>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleFieldChange('brochure', null)}
                              className="text-xs text-blue-600 hover:text-blue-800 underline whitespace-nowrap"
                            >
                              Remove
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Submit Button */}
                <div className="flex justify-end gap-4 pt-6 border-t border-[var(--neutral-medium-gray)]">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setFormData(emptyTourForm());
                      setErrors([]);
                      setSubmitStatus(null);
                    }}
                  >
                    Reset Form
                  </button>
                  
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn btn-primary"
                  >
                    {isSubmitting 
                      ? (isUpdateMode ? 'Updating Tour...' : 'Creating Tour...') 
                      : (isUpdateMode ? 'Update Tour' : 'Create Tour')
                    }
                  </button>
                </div>

                {/* Status Message */}
                {submitStatus && (
                  <div className={`p-4 rounded-md ${
                    submitStatus.includes('successfully') 
                      ? 'bg-green-100 text-green-800 border border-green-200' 
                      : 'bg-red-100 text-red-800 border border-red-200'
                  }`}>
                    <p>{submitStatus}</p>
                    {!submitStatus.includes('successfully') &&
                      (isFileSizeError(submitStatus) ||
                        isFileSizeError(getFieldError('banner')) ||
                        isFileSizeError(getFieldError('brochure'))) &&
                      (formData.banner || formData.brochure) && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {formData.banner && (
                          <button
                            type="button"
                            onClick={() => handleFieldChange('banner', null)}
                            className="px-3 py-1.5 text-xs font-medium bg-white text-red-800 border border-red-300 rounded-md hover:bg-red-50"
                          >
                            Remove banner image
                          </button>
                        )}
                        {formData.brochure && (
                          <button
                            type="button"
                            onClick={() => handleFieldChange('brochure', null)}
                            className="px-3 py-1.5 text-xs font-medium bg-white text-red-800 border border-red-300 rounded-md hover:bg-red-50"
                          >
                            Remove brochure
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </form>
            </div>
          </div>

          {/* Tour List Section */}
          <div className="mt-12">
            <div className="mb-6">
              <h2 className="heading-secondary text-[var(--primary-deep-blue)] mb-2">
                All Tours
              </h2>
              <p className="body-regular text-[var(--secondary-slate)]">
                Active tours appear on the website. After the return date, tours move to Archived for 2 years, then are deleted automatically.
              </p>
            </div>
            
            <TourList 
              tours={tours} 
              onUpdateTour={handleUpdateTour}
              onDeleteTour={handleDeleteTour}
              loading={toursLoading}
            />
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default AdminTourManagementPage;
